import React, { createContext, useCallback, useMemo, useReducer } from 'react'
import { initialValues } from './LoginRegistration/Registration/RegistrationUtil'
import { Venue_Type } from './Helper/RegistrationHelper'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

// fallback phone regex (E.164-ish: optional +, 7-15 digits)
const isPhoneFallback = /^\+?[0-9]{7,15}$/

const isText = /^[A-Z ]+$/i
const isEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
const isZip = /^[0-9]{5}([- /]?[0-9]{4})?$/ // us
const isNumber = /^\d+$/
const isSymbCharDigllow = /^(https?:\/\/)?([A-Z0-9.-]+\.[A-Z]{2,})(\/[A-Z0-9._%+-]*)*$/i

export const AppContext = createContext({
  activeStep: 0,
  formValues: initialValues,
  handleChange() {},
  handleNext() {},
  handleBack() {},
  goToStep() {},
  setFormValue() {},
  setFormValues() {}
})

function reducer(state, action) {
  switch (action.type) {
    case 'increase':
      return {
        ...state,
        activeStep: state.activeStep + 1
      }
    case 'decrease':
      return {
        ...state,
        activeStep: state.activeStep - 1
      }
    case 'go-to-step':
      return {
        ...state,
        activeStep: action.step
      }
    case 'form-value':
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [action.name]: {
            // preserve existing shape if present, otherwise create minimal object
            ...(state.formValues[action.name] || {}),
            value: action.fieldValue
          }
        }
      }
    case 'form-error':
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [action.name]: {
            ...(state.formValues[action.name] || {}),
            error: action.error
          }
        }
      }

    default:
      return state
  }
}

export default function StepsProvider({ children }) {
  const [{ activeStep, formValues }, dispatch] = useReducer(reducer, {
    activeStep: 0,
    formValues: initialValues
  })

  // Proceed to next step
  const handleNext = useCallback(() => dispatch({ type: 'increase' }), [])
  // Go back to prev step
  const handleBack = useCallback(() => dispatch({ type: 'decrease' }), [])
  // Jump to a specific wizard step (used when resuming incomplete registration)
  const goToStep = useCallback(
    (step) => dispatch({ type: 'go-to-step', step }),
    []
  )

  // Field validation helper (kept inside provider so we can dispatch errors)
  const validateField = useCallback((name, value, checked = false) => {
    const fieldDef = initialValues[name]
    if (!fieldDef) {
      // No validation rules known for this field; clear any previous error
      dispatch({ type: 'form-error', name, error: '' })
      return
    }

    const { required, validate, minLength, maxLength, helperText } = fieldDef

    let error = ''

    if (required && (value === undefined || value === null || value === '')) {
      error = 'This field is required'
    }
    if (!error && minLength && value && String(value).length < minLength) {
      error = `Minimum ${minLength} characters is required.`
    }
    if (!error && maxLength && value && String(value).length > maxLength) {
      error = 'Maximum length exceeded!'
    }

    if (!error && validate) {
      switch (validate) {
        case 'username':
        case 'venuename':
        case 'venueCurrency':
        case 'venuecountry':
        case 'venuestate':
        case 'venuecity':
        case 'venueaddress':
          if (value && !isText.test(value)) error = helperText || 'This field accepts text only.'
          break

        case 'venuewebsiteurl':
          if (value && !isSymbCharDigllow.test(value)) error = helperText || 'Please enter a valid URL.'
          break

        case 'phone':
        case 'venuemobileno':
          if (value) {
            // try libphonenumber-js parsing first
            try {
              const phone = parsePhoneNumberFromString(String(value))
              if (phone && phone.isValid()) {
                // valid
              } else {
                // fallback to digits length + simple pattern
                const digits = String(value).replace(/\D/g, '')
                if (!(digits.length >= 7 && digits.length <= 15) || !isPhoneFallback.test(String(value))) {
                  error = helperText || 'Please enter a valid phone number.'
                }
              }
            } catch (e) {
              const digits = String(value).replace(/\D/g, '')
              if (!(digits.length >= 7 && digits.length <= 15) || !isPhoneFallback.test(String(value))) {
                error = helperText || 'Please enter a valid phone number.'
              }
            }
          } else if (required) {
            error = helperText || 'Please enter a valid phone number.'
          }
          break

        case 'email':
        case 'venueemail':
          if (value && !isEmail.test(value)) error = helperText || 'Please enter a valid email address.'
          break

        case 'text':
          if (value && !isText.test(value)) error = helperText || 'This field accepts text only.'
          break

        case 'number':
          if (value && !isNumber.test(String(value))) error = helperText || 'This field accepts numbers only.'
          break

        case 'zip':
        case 'venuepincode':
          if (value && !isZip.test(String(value))) error = helperText || 'Please enter a valid zip code.'
          break

        case 'checkbox':
          if (!checked) error = helperText || 'Please provide a valid value.'
          break

        case 'select':
        case Venue_Type:
          if (!value) error = helperText || 'Please select a value.'
          break

        default:
          break
      }
    }

    dispatch({ type: 'form-error', name, error })
  }, [])

  // Programmatic setter(s) so components don't mutate formValues directly
  const setFormValue = useCallback((name, value) => {
    dispatch({ type: 'form-value', name, fieldValue: value })
    // run validation after setting value
    // reuse validation logic by delegating to a lightweight validator (below)
    validateField(name, value)
  }, [validateField])

  const setFormValues = useCallback((obj) => {
    Object.entries(obj).forEach(([name, value]) => {
      dispatch({ type: 'form-value', name, fieldValue: value })
      validateField(name, value)
    })
  }, [validateField])

  // Handle form change
  // Accepts either:
  //  - a DOM-like event: handleChange(event)
  //  - programmatic: handleChange('fieldName', value)
  const handleChange = useCallback((eventOrName, maybeValueOrChecked) => {
    // if first arg is a string, treat as (name, value)
    if (typeof eventOrName === 'string') {
      const name = eventOrName
      const value = maybeValueOrChecked
      dispatch({ type: 'form-value', name, fieldValue: value })
      validateField(name, value)
      return
    }

    // otherwise assume it's an event object from an input
    const event = eventOrName || {}
    const { type, name } = event.target || {}
    let value = event.target ? event.target.value : undefined
    const checked = typeof maybeValueOrChecked !== 'undefined' ? maybeValueOrChecked : (event.target ? event.target.checked : false)

    // if input type is checkbox, use checked
    const fieldValue = type === 'checkbox' ? checked : value

    if (!name) return // defensive

    dispatch({ type: 'form-value', name, fieldValue })
    validateField(name, fieldValue, checked)
  }, [validateField])

  const constextValue = useMemo(
    () => ({
      activeStep,
      formValues,
      handleChange,
      handleNext,
      handleBack,
      goToStep,
      setFormValue,
      setFormValues
    }),
    [activeStep, formValues, handleChange, handleNext, handleBack, goToStep, setFormValue, setFormValues]
  )

  return (
    <AppContext.Provider value={constextValue}>
      <div className='mui-step-form'>{children}</div>
    </AppContext.Provider>
  )
}
